import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, MessageSquare, AlertCircle, Loader2, Lightbulb, CheckCircle2 } from 'lucide-react';
import SEO from '../components/seo/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: 'situation', label: 'The Situation', icon: AlertCircle, accent: 'bg-amber-100 text-amber-700' },
  { key: 'scenario', label: 'The Scenario', icon: AlertCircle, accent: 'bg-amber-100 text-amber-700' },
  { key: 'what_is_really_going_on', label: "What's Really Going On", icon: Lightbulb, accent: 'bg-indigo-100 text-indigo-700' },
  { key: 'answer', label: 'The Answer', icon: CheckCircle2, accent: 'bg-emerald-100 text-emerald-700' },
  { key: 'what_to_do_next', label: 'What to Do Next', icon: CheckCircle2, accent: 'bg-emerald-100 text-emerald-700' },
  { key: 'common_mistake', label: 'Common Mistake', icon: AlertCircle, accent: 'bg-red-100 text-red-700' },
  { key: 'how_the_companion_helps', label: 'How the Companion Helps', icon: MessageSquare, accent: 'bg-blue-100 text-blue-700' },
];

const renderRichText = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  const blocks = [];
  let listBuffer = [];
  let paraBuffer = [];
  const flushPara = () => {
    if (paraBuffer.length) {
      blocks.push(<p key={`p-${blocks.length}`} className="text-stone-700 leading-relaxed mb-3">{paraBuffer.join(' ')}</p>);
      paraBuffer = [];
    }
  };
  const flushList = () => {
    if (listBuffer.length) {
      blocks.push(
        <ul key={`u-${blocks.length}`} className="list-disc pl-6 mb-4 space-y-1.5">
          {listBuffer.map((item, i) => (<li key={i} className="text-stone-700 leading-relaxed">{item}</li>))}
        </ul>
      );
      listBuffer = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); continue; }
    if (line.startsWith('- ')) { flushPara(); listBuffer.push(line.slice(2)); }
    else { flushList(); paraBuffer.push(line); }
  }
  flushPara(); flushList();
  return blocks;
};

const SituationDetailPage = () => {
  const { slug } = useParams();
  const [situation, setSituation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchOne = async () => {
      setLoading(true); setNotFound(false);
      try {
        const res = await fetch(`${API_URL}/api/situations-hub/${slug}`);
        if (res.status === 404) { setNotFound(true); setSituation(null); }
        else if (res.ok) setSituation(await res.json());
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    };
    fetchOne();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f0] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
      </div>
    );
  }

  if (notFound || !situation) {
    return (
      <div className="min-h-screen bg-[#f5f5f0]" data-testid="situation-not-found">
        <SEO title="Situation not found" noIndex />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-stone-900 mb-3">Situation not found</h1>
          <p className="text-stone-600 mb-6">That situation might have moved or never existed.</p>
          <Link to="/situations" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700">
            <ArrowLeft className="w-4 h-4" /> Back to Situations Hub
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f0]" data-testid="situation-detail-page">
      <SEO
        title={`${situation.title} | Situations Hub`}
        description={situation.summary}
        type="article"
      />
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Link to="/situations" className="inline-flex items-center text-red-600 hover:text-red-700 text-sm mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" /> All situations
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 leading-tight" data-testid="situation-title">{situation.title}</h1>
          {situation.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {situation.tags.map((t) => (<span key={t} className="px-2 py-0.5 text-xs rounded-full bg-stone-100 text-stone-600">{t.replace(/-/g, ' ')}</span>))}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        {SECTIONS.map(({ key, label, icon: Icon, accent }) => {
          const body = situation[key];
          if (!body) return null;
          return (
            <section key={key} className="mb-8 bg-white border border-stone-200 rounded-2xl p-6 shadow-sm" data-testid={`section-${key}`}>
              <div className="flex items-center gap-2.5 mb-4">
                <span className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${accent}`}><Icon className="w-4 h-4" /></span>
                <h2 className="text-lg font-semibold text-stone-900">{label}</h2>
              </div>
              <div>{renderRichText(body)}</div>
            </section>
          );
        })}

        {situation.source_link && <p className="text-stone-500 text-xs mb-8">Source: {situation.source_link}</p>}

        <div className="bg-stone-900 rounded-2xl p-8 text-center" data-testid="situation-cta-card">
          <MessageSquare className="w-7 h-7 text-red-400 mx-auto mb-3" />
          <h3 className="text-2xl font-semibold text-white mb-2">Bring this to your AI Companion</h3>
          <p className="text-stone-400 max-w-xl mx-auto mb-5">
            The answer above is universal. Your situation isn&apos;t. The Companion takes this same situation and works through it with you — with your team, your stakeholders, your context.
          </p>
          <Link to="/companion" data-testid="situation-companion-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700">
            Reflect with the Companion <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
    </div>
  );
};

export default SituationDetailPage;
